<template>
  <b-card class="rounded-0 mx-auto my-2 border-0">
    <component
      v-for="(componentData, i) in componentsData"
      :is="componentData.name"
      v-bind="componentData.props"
      :key="i"
    />
  </b-card>
</template>

<script>
import JobQuestionChartHistogram from '@/components/job_questions_chart_types/JobQuestionChartHistogram';
import SurveyResultsChartBar from '@/components/chart_types/SurveyResultsChartBar';
import { CHART_TYPES, CHART_VALUE_TYPES } from '@/store';
import SurveyResultsChartLollipop from '@/components/chart_types/SurveyResultsChartLollipop';
import SurveyResultsChartPie from '@/components/chart_types/SurveyResultsChartPie';
import SurveyResultsChartTreemap from '@/components/chart_types/SurveyResultsChartTreemap';
export default {
  name: 'PreferenceChartBuilder',
  props: {
    jobQuestion: {
      type: Object,
      required: true,
    },
  },
  components: {
    JobQuestionChartHistogram,
    SurveyResultsChartBar,
    SurveyResultsChartLollipop,
    SurveyResultsChartPie,
    SurveyResultsChartTreemap,
  },
  computed: {
    chartType: function () {
      if (
        this.studyQuestion &&
        this.studyQuestion.chart &&
        this.studyQuestion.chart.chart_type
      ) {
        return this.studyQuestion.chart.chart_type;
      }
      return null;
    },
    studyQuestion: function () {
      return this.jobQuestion.questions[0];
    },
    studyQuestionResults: function () {
      if (
        !this.studyQuestion ||
        !this.studyQuestion.responses ||
        !this.studyQuestion.responses.results
      ) {
        return [];
      }
      return this.studyQuestion.responses.results;
    },
    jobQuestionVariable: function () {
      return this.jobQuestion.jobQuestionVariables[0];
    },
    chartConfig: function () {
      if (this.studyQuestion.chart && this.studyQuestion.chart.chart_config) {
        return this.studyQuestion.chart.chart_config;
      }
      return null;
    },
    isPercentage: function () {
      return (
        this.chartConfig &&
        this.chartConfig.value_type &&
        this.chartConfig.value_type === CHART_VALUE_TYPES.PERCENTAGE
      );
    },
    orientation: function () {
      if (this.chartConfig && this.chartConfig.orientation) {
        return this.chartConfig.orientation;
      }
      return null;
    },
    getYTitle: function () {
      return this.isPercentage ? 'Percentage' : 'Count';
    },
    layout: function () {
      if (this.chartConfig && this.chartConfig.layout) {
        return this.chartConfig.layout;
      }
      return null;
    },
    componentsData: function () {
      switch (this.chartType) {
        case CHART_TYPES.HISTOGRAM:
          return this.studyQuestionResults.map((res, index, array) => {
            return {
              name: JobQuestionChartHistogram.name,
              props: {
                title: array.length === 1 ? '' : this.getLabelForResult(res),
                stats: res.stats,
                responses: res.responses,
                question: this.studyQuestion,
                preferenceValue:
                  this.jobQuestion.type === this.studyQuestion.type
                    ? this.getPreferenceValue(index)
                    : null,
                responsesCount: res.responses_count,
              },
            };
          });
        case CHART_TYPES.BAR:
          return this.studyQuestionResults.map((res, index, array) => {
            return {
              name: SurveyResultsChartBar.name,
              props: {
                title: array.length === 1 ? '' : this.getLabelForResult(res),
                results: res.results,
                questionType: this.studyQuestion.type,
                question: this.studyQuestion,
                stats: res.stats,
                variable: res.variable,
                isPercentage: this.isPercentage,
                orientation: this.orientation,
                yTitle: this.getYTitle,
                responsesCount: res.responses_count,
              },
            };
          });
        case CHART_TYPES.LOLLIPOP:
          return this.studyQuestionResults.map((res, index, array) => {
            return {
              name: SurveyResultsChartLollipop.name,
              props: {
                title: array.length === 1 ? '' : this.getLabelForResult(res),
                results: res.results,
                questionType: this.studyQuestion.type,
                variable: res.variable,
                isPercentage: this.isPercentage,
                orientation: this.orientation,
                yTitle: this.getYTitle,
                responsesCount: res.responses_count,
                question: this.studyQuestion,
                stats: res.stats,
              },
            };
          });
        case CHART_TYPES.PIE:
          return this.studyQuestionResults.map((res, index, array) => ({
            name: SurveyResultsChartPie.name,
            props: {
              title: array.length === 1 ? '' : this.getLabelForResult(res),
              results: res.results,
              questionType: this.studyQuestion.type,
              format: this.format,
              responsesCount: res.responses_count,
              question: this.studyQuestion,
              stats: res.stats,
            },
          }));
        case CHART_TYPES.TREEMAP:
          return this.studyQuestionResults.map((res, index, array) => {
            return {
              name: SurveyResultsChartTreemap.name,
              props: {
                title: array.length === 1 ? '' : this.getLabelForResult(res),
                results: res.results,
                questionType: this.studyQuestion.type,
                layout: this.layout,
                responsesCount: res.responses_count,
                question: this.studyQuestion,
                stats: res.stats,
              },
            };
          });
        default:
          return [];
      }
    },
  },
  methods: {
    getPreferenceValue: function (index) {
      const jobQuestionVariable = this.jobQuestion.jobQuestionVariables[index];
      if (!jobQuestionVariable) return 0;
      const jobProfileQuestionResponse =
        jobQuestionVariable.jobProfileQuestionResponses[0];
      return jobProfileQuestionResponse && jobProfileQuestionResponse.value
        ? jobProfileQuestionResponse.value
        : 0;
    },
    getLabelForResult: function (res) {
      if (res.label) return res.label;
      return res.variable
        ? res.variable.short_label || res.variable.input_label
        : '';
    },
  },
};
</script>

<style scoped></style>
